// Reverb / WebSocket disconnect fallback for the participant page.
//
// Why this exists:
//   The participant view is push-driven via Echo (`question.released`,
//   `question.closed`, `session.ended`). If Reverb crashes mid-session or the
//   user's network drops, participants would otherwise freeze on the last-
//   seen question with no recourse.
//
// Strategy:
//   - Wait `DISCONNECT_THRESHOLD_MS` after a disconnect before reacting (small
//     blips < 10 s are common on mobile and don't warrant a refresh).
//   - Once threshold elapses, poll `/s/{code}/state.json` every
//     `POLL_INTERVAL_MS`.
//   - If the server-reported `current_question_id` or `status` differs from
//     what the page rendered, force a full reload. This is intentionally
//     coarse: a reload guarantees the Livewire component re-syncs with the
//     server. The alternative (synthesising fake Echo events) is fragile.
//   - When Echo reconnects, the polling loop tears down.

const DISCONNECT_THRESHOLD_MS = 10_000;
const POLL_INTERVAL_MS = 5_000;

export default function installEchoFallback() {
    // Only run on the participant page — match `/s/{CODE}` URLs only.
    const match = window.location.pathname.match(/^\/s\/([A-Z0-9]+)\/?$/i);
    if (!match) return;
    const code = match[1];

    // Capture the initial state the page rendered with so we know what
    // "changed" means while polling.
    let initial = null;
    try {
        const root = document.querySelector('[data-session-state]');
        if (root) {
            initial = JSON.parse(root.dataset.sessionState || '{}');
        }
    } catch {
        // Best-effort parse; if it fails, polling will still detect any
        // observed change by comparing successive snapshots.
    }
    let lastSeen = initial;

    let disconnectTimer = null;
    let pollTimer = null;

    function startPolling() {
        if (pollTimer !== null) return;
        pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);
        // Fire immediately on entry so the user doesn't wait one full
        // interval before getting any signal.
        pollOnce();
    }

    function stopPolling() {
        if (pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    async function pollOnce() {
        try {
            const res = await fetch(`/s/${code}/state.json`, {
                cache: 'no-store',
                credentials: 'same-origin',
            });
            if (!res.ok) return;
            const state = await res.json();

            if (lastSeen
                && (state.current_question_id !== lastSeen.current_question_id
                    || state.status !== lastSeen.status)) {
                // Server state diverged from what the page knows — a reload
                // is the cheapest way to re-sync the Livewire snapshot.
                window.location.reload();
                return;
            }
            lastSeen = state;
        } catch {
            // Network still broken — keep trying next tick.
        }
    }

    // Echo / pusher-js exposes `window.Echo`. Bind once it's ready (the
    // bootstrap file constructs it synchronously, so it should be present
    // by the time this runs, but guard anyway).
    function bindEchoStateHandlers() {
        if (!window.Echo || !window.Echo.connector || !window.Echo.connector.pusher) {
            // Try again next tick — bootstrap might not have run yet.
            setTimeout(bindEchoStateHandlers, 200);
            return;
        }
        const pusher = window.Echo.connector.pusher;

        pusher.connection.bind('disconnected', onDisconnect);
        pusher.connection.bind('unavailable', onDisconnect);
        pusher.connection.bind('failed', onDisconnect);
        pusher.connection.bind('connected', onReconnect);
    }

    function onDisconnect() {
        if (disconnectTimer !== null) return; // already armed
        disconnectTimer = setTimeout(() => {
            disconnectTimer = null;
            startPolling();
        }, DISCONNECT_THRESHOLD_MS);
    }

    function onReconnect() {
        if (disconnectTimer !== null) {
            clearTimeout(disconnectTimer);
            disconnectTimer = null;
        }
        stopPolling();
    }

    bindEchoStateHandlers();
}
